import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Package, Calendar, DollarSign, MapPin, User, Phone, Mail } from 'lucide-react';
import './OrderHistory.css';

function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      // In a real app, you'd filter by the current user's email
      const ordersQuery = query(
        collection(db, 'orders'),
        orderBy('createdAt', 'desc')
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      shipped: '#8b5cf6',
      delivered: '#10b981',
      cancelled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="order-history-loading">
        <div className="spinner"></div>
        <p>Loading your orders...</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="empty-orders">
        <Package size={64} color="#d1d5db" />
        <h2>No orders yet</h2>
        <p>Start shopping to see your order history here!</p>
      </div>
    );
  }

  return (
    <div className="order-history">
      <div className="order-history-header">
        <h1>My Orders</h1>
        <p>Track and manage your orders</p>
      </div>

      <div className="order-history-content">
        <div className="orders-list">
          {orders.map(order => (
            <div key={order.id} className="order-card-expanded">
              <div className="order-header">
                <div className="order-id">Order #{order.id.slice(-6).toUpperCase()}</div>
                <div
                  className="order-status-badge"
                  style={{ backgroundColor: getStatusColor(order.status) }}
                >
                  {order.status}
                </div>
              </div>

              <div className="order-info">
                <div className="info-item">
                  <Calendar size={16} />
                  <span>{formatDate(order.createdAt)}</span>
                </div>
                <div className="info-item">
                  <DollarSign size={16} />
                  <span className="order-total">${order.total?.toFixed(2)}</span>
                </div>
              </div>

              <div className="order-shipping">
                <div className="info-item">
                  <MapPin size={16} />
                  <span>{order.shippingAddress?.street}, {order.shippingAddress?.city}</span>
                </div>
                {order.trackingNumber && (
                  <div className="info-item">
                    <Package size={16} />
                    <span>Tracking: {order.trackingNumber}</span>
                  </div>
                )}
              </div>

              <div className="order-items">
                <h4>Items ({order.items?.length})</h4>
                <div className="items-list">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="order-item">
                      <div className="item-info">
                        <span className="item-name">{item.name}</span>
                        <span className="item-price">${item.price} x {item.quantity}</span>
                      </div>
                      <span className="item-total">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="order-footer">
                <button 
                  className="track-order-btn"
                  onClick={() => window.open(`/track/${order.id}`, '_blank')}
                >
                  Track Order
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OrderHistory;
